/**
 * خدمة حفظ واسترجاع الملفات المختارة في التخزين المحلي
 * تتعامل مع تحويل الملفات إلى Base64 وحفظها محلياً
 */

const FILE_STORAGE_KEY = 'selectedFiles';

/**
 * تحويل ملف إلى Base64
 * @param {File} file - الملف المراد تحويله
 * @returns {Promise<string>} - الملف بصيغة Base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * تحويل Base64 إلى ملف
 * @param {string} dataUrl - البيانات بصيغة Base64
 * @param {string} filename - اسم الملف
 * @param {string} mimeType - نوع الملف
 * @returns {File} - الملف المستعاد
 */
const base64ToFile = (dataUrl, filename, mimeType) => {
  const arr = dataUrl.split(',');
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mimeType });
};

/**
 * حفظ الملفات المختارة في التخزين المحلي
 * @param {Object} documents - كائن الملفات المختارة
 * @returns {Promise<Object>} - نتيجة عملية الحفظ
 */
export const saveSelectedFiles = async (documents) => {
  try {
    if (!documents || typeof documents !== 'object') {
      return { success: false, message: 'لا توجد ملفات للحفظ' };
    }

    const savedFiles = {};
    
    for (const [key, docData] of Object.entries(documents)) {
      if (docData && docData.file && docData.file instanceof File) {
        try {
          // تحويل الملف إلى Base64
          const base64Data = await fileToBase64(docData.file);
          
          savedFiles[key] = {
            name: docData.name,
            type: docData.type,
            size: docData.size,
            base64Data: base64Data,
            savedAt: new Date().toISOString()
          };
        } catch (error) {
          console.error(`خطأ في حفظ الملف ${key}:`, error);
          // نتجاهل الملفات التي فشل حفظها ونكمل مع الباقي
        }
      } else if (docData && !docData.file) {
        // إذا كان الملف محفوظ مسبقاً (له معرف أو رابط)
        savedFiles[key] = {
          ...docData,
          savedAt: docData.savedAt || new Date().toISOString()
        };
      }
    }

    // حفظ البيانات في التخزين المحلي
    localStorage.setItem(FILE_STORAGE_KEY, JSON.stringify(savedFiles));
    
    return {
      success: true,
      message: 'تم حفظ الملفات بنجاح',
      savedCount: Object.keys(savedFiles).length
    };
  } catch (error) {
    console.error('خطأ في حفظ الملفات:', error);
    return {
      success: false,
      message: 'فشل في حفظ الملفات: ' + error.message
    };
  }
};

/**
 * استرجاع الملفات المحفوظة من التخزين المحلي
 * @returns {Promise<Object>} - الملفات المسترجعة
 */
export const loadSelectedFiles = async () => {
  try {
    const savedData = localStorage.getItem(FILE_STORAGE_KEY);
    if (!savedData) {
      return null;
    }

    const parsedData = JSON.parse(savedData);
    const restoredFiles = {};

    for (const [key, fileData] of Object.entries(parsedData)) {
      if (fileData.base64Data) {
        try {
          // استعادة الملف من Base64
          const file = base64ToFile(fileData.base64Data, fileData.name, fileData.type);
          
          restoredFiles[key] = {
            file: file,
            name: fileData.name,
            type: fileData.type,
            size: fileData.size,
            savedAt: fileData.savedAt
          };
        } catch (error) {
          console.error(`خطأ في استرجاع الملف ${key}:`, error);
          // نتجاهل الملفات التي فشل استرجاعها
        }
      } else {
        // ملف محفوظ على الخادم (له معرف أو رابط)
        restoredFiles[key] = fileData;
      }
    }

    return restoredFiles;
  } catch (error) {
    console.error('خطأ في استرجاع الملفات:', error);
    return null;
  }
};

/**
 * حذف الملفات المحفوظة من التخزين المحلي
 * @returns {boolean} - نتيجة عملية الحذف
 */
export const clearSelectedFiles = () => {
  try {
    localStorage.removeItem(FILE_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('خطأ في حذف الملفات:', error);
    return false;
  }
};

/**
 * التحقق من وجود ملفات محفوظة
 * @returns {boolean} - هل توجد ملفات محفوظة
 */
export const hasSelectedFiles = () => {
  try {
    const savedData = localStorage.getItem(FILE_STORAGE_KEY);
    return savedData !== null && savedData !== 'null';
  } catch (error) {
    return false;
  }
};

/**
 * الحصول على معلومات الملفات المحفوظة
 * @returns {Object|null} - معلومات الملفات
 */
export const getSelectedFilesInfo = () => {
  try {
    const savedData = localStorage.getItem(FILE_STORAGE_KEY);
    if (!savedData) {
      return null;
    }

    const parsedData = JSON.parse(savedData);
    const info = {
      count: Object.keys(parsedData).length,
      files: {},
      totalSize: 0
    };

    for (const [key, fileData] of Object.entries(parsedData)) {
      info.files[key] = {
        name: fileData.name,
        type: fileData.type,
        size: fileData.size,
        savedAt: fileData.savedAt
      };
      info.totalSize += fileData.size || 0;
    }

    return info;
  } catch (error) {
    console.error('خطأ في الحصول على معلومات الملفات:', error);
    return null;
  }
};

/**
 * تنظيف الملفات القديمة (أكثر من 7 أيام)
 */
export const cleanupOldFiles = () => {
  try {
    const savedData = localStorage.getItem(FILE_STORAGE_KEY);
    if (!savedData) {
      return;
    }

    const parsedData = JSON.parse(savedData);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    let hasOldFiles = false;
    for (const fileData of Object.values(parsedData)) {
      if (fileData.savedAt) {
        const savedDate = new Date(fileData.savedAt);
        if (savedDate < sevenDaysAgo) {
          hasOldFiles = true;
          break;
        }
      }
    }

    if (hasOldFiles) {
      localStorage.removeItem(FILE_STORAGE_KEY);
      console.log('تم حذف الملفات القديمة من التخزين المحلي');
    }
  } catch (error) {
    console.error('خطأ في تنظيف الملفات القديمة:', error);
    localStorage.removeItem(FILE_STORAGE_KEY);
  }
};

/**
 * حساب الحجم الإجمالي للملفات المحفوظة
 * @returns {number} - الحجم بالبايت
 */
export const getStorageSize = () => {
  try {
    const savedData = localStorage.getItem(FILE_STORAGE_KEY);
    if (!savedData) {
      return 0;
    }
    
    // حساب حجم البيانات المحفوظة (تقريبي)
    return new Blob([savedData]).size;
  } catch (error) {
    return 0;
  }
};
